import React from 'react';
import { Link } from 'react-router-dom';
import BlogLayout from './BlogLayout';
import { postBySlug } from './posts';

const post = postBySlug('client-side-document-processing-is-safer');

const PostClientSideSafer = () => (
    <BlogLayout post={post}>
        <p>
            You drag a scanned passport into an online &quot;PDF to JPG&quot; tool. A spinner
            runs for a few seconds. A download link appears. You click it, save the images, and
            move on with your day. What just happened?
        </p>
        <p>
            Your passport was uploaded to a server in a datacenter you&rsquo;ve never heard of.
            It was processed there. It was stored, at minimum, in RAM and probably on disk. It
            may have been cached by a CDN, logged by a reverse proxy, and occasionally peeked
            at by a site operator debugging &quot;why do some PDFs break the converter.&quot;
            You have no way to verify any of that.
        </p>
        <p>
            Client-side document processing is a technical answer to that problem. This post
            explains what it is, how it works, and what it genuinely protects — and what it
            doesn&rsquo;t.
        </p>

        <h2>What client-side processing means</h2>
        <p>
            When you use a traditional online tool, the data flow is:
        </p>
        <ol>
            <li>Your browser uploads the file to the website&rsquo;s server.</li>
            <li>The server runs processing software against it.</li>
            <li>The server produces an output file and sends it back to your browser.</li>
        </ol>
        <p>
            When you use a client-side tool, the data flow is:
        </p>
        <ol>
            <li>The website&rsquo;s server sends your browser some JavaScript.</li>
            <li>That JavaScript, running in your browser, processes your file.</li>
            <li>Your browser writes the output to your downloads folder.</li>
        </ol>
        <p>
            The key difference: your file never traverses the network. It stays in the
            browser&rsquo;s memory on your device. The website&rsquo;s server never sees it,
            never touches it, and has nothing to retain.
        </p>

        <h2>How this is technically possible</h2>
        <p>
            A decade ago, this would have been a nice idea but impractical. JavaScript was too
            slow for PDF parsing, and browsers didn&rsquo;t expose the filesystem APIs you&rsquo;d
            need. Three things changed:
        </p>

        <h3>1. JavaScript got fast</h3>
        <p>
            Modern JavaScript engines (V8 in Chrome, SpiderMonkey in Firefox, JavaScriptCore in
            Safari) produce machine code that runs within a factor of two or three of native C.
            That&rsquo;s fast enough for real work.
        </p>

        <h3>2. WebAssembly appeared</h3>
        <p>
            For the truly performance-critical code — say, zlib compression or PDF rendering —
            developers can compile C and Rust libraries to <strong>WebAssembly</strong>, a
            portable binary format that runs inside the browser sandbox at roughly native
            speed. The C library <code>pdfium</code> (used by Chrome) and its derivatives all
            have WebAssembly ports. So does image processing, archive extraction, and even
            video encoding.
        </p>

        <h3>3. Browsers gained proper file APIs</h3>
        <p>
            The <code>File</code>, <code>Blob</code>, and <code>FileReader</code> APIs give
            JavaScript running in the browser direct access to files the user explicitly picks.
            The browser can read bytes, process them, and hand back a generated file for
            download — all without a network round-trip.
        </p>

        <h2>What client-side processing genuinely protects</h2>

        <h3>Your file doesn&rsquo;t exist on the operator&rsquo;s infrastructure</h3>
        <p>
            This is the headline benefit. Even if the operator is malicious, careless, or
            compromised, there&rsquo;s nothing for them to leak. The file simply wasn&rsquo;t
            on their machines.
        </p>

        <h3>Nothing to subpoena</h3>
        <p>
            Law enforcement and civil litigants can compel a company to hand over data it has.
            They cannot compel it to hand over data it never had. For people in certain
            industries (journalists working with sources, lawyers handling privileged
            documents, clinicians with patient records), this matters.
        </p>

        <h3>No server-side retention policy surprises</h3>
        <p>
            Online converters&rsquo; privacy policies often say &quot;we delete your file after
            1 hour&quot; or &quot;after 24 hours.&quot; That&rsquo;s a promise, not a guarantee,
            and you have no way to verify it. With client-side processing, the question
            doesn&rsquo;t apply — the server has nothing to delete.
        </p>

        <h3>Insulated from operator mistakes</h3>
        <p>
            Data breaches happen. Backups get misconfigured. Staging environments leak. A
            server-side converter is vulnerable to every mistake its operator makes. A
            client-side one isn&rsquo;t.
        </p>

        <h2>What client-side processing does NOT protect</h2>
        <p>
            Honesty matters here. Client-side tools are not a magical privacy shield.
        </p>

        <h3>Your device is still in scope</h3>
        <p>
            If your laptop is compromised — malware, a shared account, a nosy colleague over
            your shoulder — no web tool can help you. The processing happens on your machine,
            so your machine has to be trustworthy.
        </p>

        <h3>Analytics and logs still exist</h3>
        <p>
            A client-side site can still use analytics, tracking pixels, and ad networks like
            any other. It can know you visited, what browser you used, and where you came from.
            It just doesn&rsquo;t have your file. The site&rsquo;s{' '}
            <Link to="/privacy-policy">privacy policy</Link> tells you what else is collected.
        </p>

        <h3>The website&rsquo;s code itself is loaded from a server</h3>
        <p>
            The JavaScript that processes your file is delivered by the operator&rsquo;s
            server. A compromised or malicious operator could, in principle, serve code that
            uploads your file after pretending to process it locally. You&rsquo;d need to read
            the source (if available) or monitor network activity in your browser&rsquo;s
            devtools to verify. Open-source client-side tools — whose code can be audited by
            anyone — give you more confidence here than closed-source ones.
        </p>

        <h3>Only certain operations are practical</h3>
        <p>
            OCR on large scanned documents, heavy vector rendering for print, and very
            complex image manipulation are still done more efficiently server-side. For common
            cases (PDF conversion, compression, image format conversion), client-side is
            perfectly suitable.
        </p>

        <h2>How to tell whether a site is actually client-side</h2>
        <p>
            A site can claim to be client-side and quietly upload your file anyway. Here&rsquo;s
            how to verify:
        </p>
        <ol>
            <li>Open your browser&rsquo;s developer tools before using the tool.</li>
            <li>Go to the <strong>Network</strong> tab and clear it.</li>
            <li>Pick your file and run the conversion.</li>
            <li>
                Look at the network activity. If you see an upload (a <code>POST</code> request
                with a body close to your file&rsquo;s size), the tool is sending your file to
                a server.
            </li>
            <li>
                A genuinely client-side tool will show no large outbound requests during
                conversion — only small analytics or ad pings, if any.
            </li>
        </ol>
        <p>
            You can do this test on our tools too. Try it with{' '}
            <Link to="/word-to-pdf">Word to PDF</Link> or{' '}
            <Link to="/compress-pdf">Compress PDF</Link>. No file leaves your browser.
        </p>

        <h2>When you should still go server-side</h2>
        <p>
            Client-side processing isn&rsquo;t the right answer for every situation. Valid
            reasons to use a server-side tool include:
        </p>
        <ul>
            <li>
                <strong>OCR on scanned documents.</strong> The models are too large and
                slow to run in-browser for multi-page scans.
            </li>
            <li>
                <strong>Collaborative workflows.</strong> If multiple people need to sign or
                comment on a document, a server is a natural meeting point.
            </li>
            <li>
                <strong>Machine with limited RAM.</strong> A cheap Chromebook may struggle with
                a 500 MB PDF; a server won&rsquo;t.
            </li>
        </ul>
        <p>
            In those cases, pick a reputable service, read its privacy policy, and understand
            the trade-off. The fact that you&rsquo;re <em>choosing</em> to trust a provider with
            your file — rather than doing so unknowingly — is itself a privacy win.
        </p>

        <h2>Summary</h2>
        <p>
            Client-side document processing is not a marketing slogan; it&rsquo;s a concrete
            architectural decision that meaningfully reduces who has access to your files. It
            doesn&rsquo;t make you invincible, and it doesn&rsquo;t fit every workflow. For
            everyday conversions of sensitive documents — contracts, medical records, personal
            IDs, drafts of unpublished work — it&rsquo;s a genuinely better default than an
            upload-based tool.
        </p>
    </BlogLayout>
);

export default PostClientSideSafer;
